import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { MessageCircle, ShoppingBag, CalendarCheck } from "lucide-react";
import { toast } from "sonner";
import { getClient, postCheckin, AREA_LABEL } from "./clubApi";
import { Btn, Card, ScoreRing, Bar, Pill, Footer, Logo, inputCls } from "./ui";
import { InstallHint } from "./TestResult";
import ProgressChart from "./ProgressChart";

const fmt = (iso) => new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short" });

const Checkin = ({ token, onDone }) => {
  const [v, setV] = useState({ energia: 3, sonno: 3, costanza: 3, nota: "" });
  const [busy, setBusy] = useState(false);
  const Row = ({ k, label }) => (
    <div>
      <p className="text-xs uppercase tracking-[0.18em] text-[#9A948A] mb-2">{label}</p>
      <div className="grid grid-cols-5 gap-2">
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} type="button" data-testid={`checkin-${k}-${n}`} onClick={() => setV({ ...v, [k]: n })}
            className={`h-11 rounded-xl border font-club-display text-lg ${v[k] === n ? "border-[#E5B13A] bg-[#E5B13A] text-[#15120A]" : "border-[#2E2E34] bg-[#0E0E10]"}`}>{n}</button>
        ))}
      </div>
    </div>
  );
  const send = async () => {
    setBusy(true);
    try { const ck = await postCheckin(token, v); toast.success("Check salvato"); onDone(ck); } catch { toast.error("Non riesco a salvare, riprova"); }
    setBusy(false);
  };
  return (
    <Card className="space-y-5" data-testid="checkin-form">
      <div className="flex items-center gap-2"><CalendarCheck size={18} className="text-[#E5B13A]" /><p className="font-semibold">Il check della settimana</p></div>
      <Row k="energia" label="Energia" /><Row k="sonno" label="Sonno" /><Row k="costanza" label="Costanza con piano e abitudini" />
      <textarea className={`${inputCls} min-h-[72px]`} placeholder="Una nota per la tua incaricata (facoltativa)" value={v.nota} onChange={(e) => setV({ ...v, nota: e.target.value })} data-testid="checkin-nota" />
      <Btn className="w-full" disabled={busy} onClick={send} data-testid="checkin-submit">Salva il check</Btn>
    </Card>
  );
};

export default function ClientPage() {
  const { token } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [showCk, setShowCk] = useState(false);

  useEffect(() => {
    getClient(token).then(setD).catch(() => setErr("Pagina non trovata. Controlla il link che ti ha mandato la tua incaricata."));
    const l = document.createElement("link");
    l.rel = "manifest"; l.href = `${process.env.REACT_APP_BACKEND_URL}/api/club/manifest/${token}`; l.id = "club-manifest";
    document.getElementById("club-manifest")?.remove();
    document.head.appendChild(l);
    return () => l.remove();
  }, [token]);

  if (err) return <div className="mx-auto max-w-md px-6 pt-24 text-center text-[#9A948A]" data-testid="client-error">{err}</div>;
  if (!d) return <div className="min-h-screen flex items-center justify-center"><div className="w-6 h-6 border-2 border-[#E5B13A] border-t-transparent rounded-full animate-spin" /></div>;

  const { profilo, piano, checkins, incaricata, nome } = d;
  const first = nome.split(" ")[0];
  const incFirst = incaricata?.name?.split(" ")[0] || "la tua incaricata";
  const last = checkins[checkins.length - 1];
  const current = last ? last.punteggio : profilo.indice;
  const daysSince = last ? Math.floor((Date.now() - new Date(last.data)) / 86400000) : 99;

  return (
    <div className="mx-auto max-w-md px-6 pt-8 pb-10" data-testid="client-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3"><Logo size={32} /><span className="font-club-display text-lg">Diamond Club</span></div>
        {incaricata?.picture && <img src={incaricata.picture} alt="" className="h-8 w-8 rounded-full object-cover" referrerPolicy="no-referrer" />}
      </div>
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="mt-8 flex items-center gap-5">
        <ScoreRing value={current} size={132} label="oggi" />
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#9A948A]">Ciao {first}</p>
          <p className="font-club-display text-2xl leading-tight mt-1">{piano ? "Il tuo percorso è attivo." : "Il tuo percorso sta arrivando."}</p>
          {last && <p className="text-xs text-[#9A948A] mt-2" data-testid="score-delta">Partenza {profilo.indice} → oggi {current} ({current - profilo.indice >= 0 ? "+" : ""}{current - profilo.indice})</p>}
        </div>
      </motion.div>

      {piano ? (
        <Card className="mt-8" data-testid="client-piano">
          <div className="flex items-center justify-between"><p className="font-semibold">Il tuo percorso di {piano.durata_giorni || 30} giorni</p><Pill tone="green">attivo</Pill></div>
          <div className="mt-4 space-y-3">
            {piano.prodotti.map((p) => (
              <div key={p.codice || p.nome} className="rounded-2xl bg-[#0E0E10] border border-[#232328] p-3.5" data-testid="piano-prodotto">
                <p className="font-medium text-sm">{p.nome}</p>
                {p.dose && <p className="text-xs text-[#9A948A] mt-1">{p.dose}</p>}
                {p.fine_stimata && <p className="text-[0.65rem] uppercase tracking-[0.15em] text-[#6F6A62] mt-2">finisce intorno al {fmt(p.fine_stimata)}</p>}
              </div>
            ))}
          </div>
          {piano.note && <p className="mt-4 text-sm text-[#F4EFE6]/90 italic border-l-2 border-[#E5B13A] pl-3">"{piano.note}" — {incFirst}</p>}
          {incaricata?.forever_link && (
            <a href={incaricata.forever_link} target="_blank" rel="noreferrer" data-testid="order-link"><Btn className="mt-5 w-full"><ShoppingBag size={16} /> Ordina con il link di {incFirst}</Btn></a>
          )}
        </Card>
      ) : (
        <Card className="mt-8 border-dashed" data-testid="client-piano-pending">
          <p className="font-semibold">Percorso in preparazione</p>
          <p className="text-sm text-[#9A948A] mt-1">{incFirst} sta guardando le tue risposte. Intanto parti dalle tre abitudini qui sotto: contano più di qualsiasi prodotto.</p>
        </Card>
      )}

      <div className="mt-8">
        <p className="font-club-display text-2xl">Le tue tre abitudini</p>
        <div className="mt-4 space-y-3">
          {profilo.abitudini.map((h, i) => (
            <Card key={h.area} className="flex gap-4"><span className="font-club-display text-2xl text-[#E5B13A]">0{i + 1}</span><div><Pill tone="muted">{h.label}</Pill><p className="mt-2 text-sm leading-relaxed">{h.testo}</p></div></Card>
          ))}
        </div>
      </div>

      <div className="mt-8">
        <div className="flex items-center justify-between">
          <p className="font-club-display text-2xl">Come va</p>
          {piano && !showCk && <Btn variant={daysSince >= 7 ? "gold" : "ghost"} className="px-4 py-2 text-xs" onClick={() => setShowCk(true)} data-testid="open-checkin-btn">Fai il check</Btn>}
        </div>
        {!piano && <Card className="mt-4 text-sm text-[#9A948A]" data-testid="check-not-yet">Il primo check arriva dopo la prima settimana di percorso. Intanto, le tre abitudini.</Card>}
        {showCk && <div className="mt-4"><Checkin token={token} onDone={(ck) => { setD({ ...d, checkins: [...checkins, ck] }); setShowCk(false); }} /></div>}
        {checkins.length > 0 && <ProgressChart start={profilo.indice} startDate={d.created_at} checkins={checkins} />}
        {checkins.length > 0 && (
          <Card className="mt-4 space-y-2" data-testid="checkin-history">
            {[...checkins].reverse().slice(0, 6).map((c, i) => (
              <div key={c.data} className="flex items-center justify-between text-sm border-b border-[#232328] last:border-0 pb-2 last:pb-0">
                <span className="text-[#9A948A]">{fmt(c.data)}</span>
                <span className="text-xs text-[#9A948A]">E{c.energia} · S{c.sonno} · C{c.costanza}</span>
                <span className="font-club-display text-lg text-[#E5B13A]">{c.punteggio}</span>
              </div>
            ))}
          </Card>
        )}
      </div>

      <Card className="mt-8 space-y-3">
        <p className="text-xs uppercase tracking-[0.2em] text-[#9A948A] mb-1">Le tue aree alla partenza</p>
        {Object.entries(profilo.aree).map(([k, v]) => <Bar key={k} label={AREA_LABEL[k]} value={v} />)}
      </Card>

      <div className="mt-8 space-y-3">
        {incaricata?.whatsapp && (
          <a href={`https://wa.me/${incaricata.whatsapp}?text=${encodeURIComponent(`Ciao ${incFirst}, ti scrivo dalla mia pagina Diamond Club.`)}`} target="_blank" rel="noreferrer" data-testid="client-wa-link">
            <Btn variant="wa" className="w-full"><MessageCircle size={16} /> Scrivi a {incFirst}</Btn>
          </a>
        )}
        <InstallHint />
      </div>
      <Footer />
    </div>
  );
}
